import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Discipline, DisciplineListResponse, CreateDisciplineRequest, SyllabusItem } from '../models/discipline.model';

@Injectable({
  providedIn: 'root'
})
export class DisciplineService {
  private apiUrl = `${environment.apiUrl}/admin/disciplinas`;

  constructor(private http: HttpClient) {}

  /**
   * List all disciplines or search by paginated name
   * @param name - Optional search term
   * @param page - Page number (default: 0)
   * @param size - Page size (default: 20)
   * @returns Observable containing the paginated discipline list
   */
  listDisciplines(name?: string, page: number = 0, size: number = 20): Observable<DisciplineListResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (name && name.trim() !== '') {
      params = params.set('nome', name.trim());
    }

    return this.http.get<DisciplineListResponse>(this.apiUrl, { params });
  }

  /**
   * Create a new discipline
   * @param disciplineData - Discipline data to create
   * @returns Observable containing the created discipline
   */
  createDiscipline(disciplineData: CreateDisciplineRequest): Observable<Discipline> {
    return this.http.post<Discipline>(this.apiUrl, disciplineData);
  }

  /**
   * Get discipline by ID
   * @param id - Discipline ID
   * @returns Observable containing the discipline
   */
  getDisciplineById(id: number): Observable<Discipline> {
    return this.http.get<Discipline>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create syllabus/content for a notice (disciplines by position)
   * POST /api/admin/editais/{editalId}/cont-programatico
   * @param editalId - Notice ID
   * @param syllabusItems - Array of syllabus items
   * @returns Observable containing the saved syllabus items
   */
  createSyllabus(editalId: number, syllabusItems: SyllabusItem[]): Observable<SyllabusItem[]> {
    return this.http.post<SyllabusItem[]>(
      `${environment.apiUrl}/admin/editais/${editalId}/cont-programatico`,
      syllabusItems
    );
  }

  /**
   * Update syllabus/content for a notice (disciplines by position)
   * PUT /api/admin/editais/{editalId}/cont-programatico
   * @param editalId - Notice ID
   * @param syllabusItems - Array of syllabus items
   * @returns Observable containing the updated syllabus items
   */
  updateSyllabus(editalId: number, syllabusItems: SyllabusItem[]): Observable<SyllabusItem[]> {
    return this.http.put<SyllabusItem[]>(
      `${environment.apiUrl}/admin/editais/${editalId}/cont-programatico`,
      syllabusItems
    );
  }

  /**
   * @deprecated Use createSyllabus or updateSyllabus instead
   * Save syllabus/content for a notice (disciplines by position)
   * POST /api/admin/editais/{editalId}/cont-programatico
   */
  saveSyllabus(editalId: number, syllabusItems: SyllabusItem[]): Observable<SyllabusItem[]> {
    return this.createSyllabus(editalId, syllabusItems);
  }

  /**
   * Get syllabus/content for a notice
   * GET /api/admin/editais/{editalId}/cont-programatico
   * @param editalId - Notice ID
   * @returns Observable containing the syllabus items
   */
  getSyllabus(editalId: number): Observable<SyllabusItem[]> {
    return this.http.get<SyllabusItem[]>(
      `${environment.apiUrl}/admin/editais/${editalId}/cont-programatico`
    );
  }
}
